<template>
  <div :class="[STATISTIC_CARD_CLASSES.listWrapper, $attrs.class]" :style="rootStyle">
    <!-- 左侧分页按钮 -->
    <StatisticPagination
      v-if="paginationPlacement === 'both' && hasPagination && (canPrev || canNext)"
      :can-prev="canPrev"
      :can-next="canNext"
      mode="prev"
      :size="mergedSize"
      @prev="goPrev"
      @next="goNext"
    />

    <!-- 带分页的内容 -->
    <div v-if="hasPagination" ref="viewportRef" :class="STATISTIC_CARD_CLASSES.pageViewport" :style="{ flex: 1, minWidth: 0, overflow: 'hidden' }">
      <div
        data-statistic-track="true"
        :class="STATISTIC_CARD_CLASSES.pageTrack"
        :style="{
          display: 'flex',
          minWidth: 0,
          transform: 'translateX(-' + pageIndex * viewportWidth + 'px)',
          transition: 'transform 300ms ease',
        }"
      >
        <div
          v-for="(startIndex, pageOffset) in pageStartIndexes"
          :key="startIndex + '-' + pageOffset"
          data-statistic-page="true"
          :data-statistic-page-active="pageOffset === pageIndex ? 'true' : 'false'"
          :aria-hidden="pageOffset !== pageIndex"
          :class="STATISTIC_CARD_CLASSES.page"
          :style="pageStyle(pageOffset)"
        >
          <!-- List layout -->
          <div
            v-if="layout === 'list'"
            :class="STATISTIC_CARD_CLASSES.listContainer"
            :style="{
              display: 'flex',
              alignItems: mergedEqualHeight ? 'stretch' : 'flex-start',
              gap: CARD_GAP + 'px',
              width: '100%',
              minWidth: 0,
            }"
          >
            <BaseStatisticCard
              v-for="item in data.slice(startIndex, startIndex + pageSize)"
              :key="item.id"
              :item="resolveCardItem(item)"
              :size="mergedSize"
              :reverse-color="mergedReverseColor"
              :border="mergedBorder"
              :checked="selection.isSelected(item.id)"
              :control-type="selectionControlType"
              :equal-height="mergedEqualHeight"
              :number-scroll="mergedNumberScroll"
              :sub-indicators-layout="mergedSubIndicatorsLayout"
              :style="getCardStyle(true)"
              :onClick="selectionControlType ? (target) => selection.toggle(target.id) : undefined"
            />
          </div>
          <!-- Grid layout -->
          <div
            v-else
            :class="STATISTIC_CARD_CLASSES.gridContainer"
            :style="{
              display: 'grid',
              gap: CARD_GAP + 'px',
              gridTemplateColumns: 'repeat(' + visibleColumnCount + ', ' + gridColumnWidth + ')',
              width: '100%',
              minWidth: 0,
              alignItems: mergedEqualHeight ? 'stretch' : 'start',
            }"
          >
            <BaseStatisticCard
              v-for="item in data.slice(startIndex, startIndex + pageSize)"
              :key="item.id"
              :item="resolveCardItem(item)"
              :size="mergedSize"
              :reverse-color="mergedReverseColor"
              :border="mergedBorder"
              :checked="selection.isSelected(item.id)"
              :control-type="selectionControlType"
              :equal-height="mergedEqualHeight"
              :number-scroll="mergedNumberScroll"
              :sub-indicators-layout="mergedSubIndicatorsLayout"
              :style="getCardStyle(pageOffset === pageIndex)"
              :onClick="selectionControlType ? (target) => selection.toggle(target.id) : undefined"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 无分页的内容 -->
    <JdScrollbar v-else :style="{ flex: 1, minWidth: 0 }">
      <!-- List layout -->
      <div
        v-if="layout === 'list'"
        :class="STATISTIC_CARD_CLASSES.listContainer"
        :style="{
          display: 'flex',
          alignItems: mergedEqualHeight ? 'stretch' : 'flex-start',
          gap: CARD_GAP + 'px',
          width: fillContainer ? '100%' : 'max-content',
          minWidth: 0,
        }"
      >
        <BaseStatisticCard
          v-for="item in data"
          :key="item.id"
          :item="resolveCardItem(item)"
          :size="mergedSize"
          :reverse-color="mergedReverseColor"
          :border="mergedBorder"
          :checked="selection.isSelected(item.id)"
          :control-type="selectionControlType"
          :equal-height="mergedEqualHeight"
          :number-scroll="mergedNumberScroll"
          :sub-indicators-layout="mergedSubIndicatorsLayout"
          :style="getCardStyle(false)"
          :onClick="selectionControlType ? (target) => selection.toggle(target.id) : undefined"
        />
      </div>
      <!-- Grid layout -->
      <div
        v-else
        :class="STATISTIC_CARD_CLASSES.gridContainer"
        :style="{
          display: 'grid',
          gap: CARD_GAP + 'px',
          gridTemplateColumns: 'repeat(' + visibleColumnCount + ', ' + gridColumnWidth + ')',
          width: '100%',
          minWidth: 0,
          alignItems: mergedEqualHeight ? 'stretch' : 'start',
        }"
      >
        <BaseStatisticCard
          v-for="item in data"
          :key="item.id"
          :item="resolveCardItem(item)"
          :size="mergedSize"
          :reverse-color="mergedReverseColor"
          :border="mergedBorder"
          :checked="selection.isSelected(item.id)"
          :control-type="selectionControlType"
          :equal-height="mergedEqualHeight"
          :number-scroll="mergedNumberScroll"
          :sub-indicators-layout="mergedSubIndicatorsLayout"
          :style="getCardStyle(true)"
          :onClick="selectionControlType ? (target) => selection.toggle(target.id) : undefined"
        />
      </div>
    </JdScrollbar>

    <!-- 右侧分页按钮 -->
    <StatisticPagination
      v-if="hasPagination && (canPrev || canNext)"
      :can-prev="canPrev"
      :can-next="canNext"
      :mode="paginationPlacement === 'single' ? 'stacked' : 'next'"
      :size="mergedSize"
      @prev="goPrev"
      @next="goNext"
    />
  </div>
</template>

<script lang="ts" setup>
// @ts-nocheck SFC 顶层复杂联合 prop(value: string|number|Array)在 dts 生成器的 SFC 运行时编译中会被降级为 `type:[null,Array]`,触发 TS2769;源码类型已由 typecheck:web 保证,沿用 dts 生成器内置的 @ts-nocheck 逃逸
import { computed, inject, provide, reactive, ref, watch, type CSSProperties } from 'vue'
import { JdScrollbar } from '@jdesign-vue/components/scrollbar'
import { statisticCardDataKey, statisticCardAppearanceKey } from '../tokens'
import { CARD_GAP, CARD_MIN_WIDTH, STATISTIC_CARD_CLASSES } from '../constants'
import { useElementWidth } from '../composables/use-element-width'
import { useSelection } from '../composables/use-selection'
import { resolvePageSize, buildPageStarts } from '../composables/use-pagination'
import BaseStatisticCard from '../base/statistic-card.vue'
import StatisticPagination from './statistic-pagination.vue'
import type {
  StatisticCardItemData,
  StatisticCardSize,
  PaginationConfig,
  StatisticCardControlType,
  StatisticCardSelectionValue,
  SelectionProps,
} from '../types'

const props = withDefaults(defineProps<{
  data: StatisticCardItemData[]
  size?: StatisticCardSize
  reverseColor?: boolean
  columns?: number
  pagination?: PaginationConfig
  layout?: 'list' | 'grid'
  border?: boolean
  equalHeight?: boolean
  numberScroll?: boolean
  cardWidth?: number | 'auto'
  minCardWidth?: number
  subIndicatorsLayout?: 'horizontal' | 'vertical'
  controlType?: StatisticCardControlType
  value?: StatisticCardSelectionValue | StatisticCardSelectionValue[]
  maxLength?: number
  minLength?: number
}>(), {
  data: () => [],
  layout: 'grid',
  // Vue Boolean 类型 prop 未传入时会被隐式转为 false,
  // 显式声明 undefined 才能让下游 `props.border ?? appearance ?? true` 的默认值链生效
  border: undefined,
  reverseColor: undefined,
  equalHeight: undefined,
  numberScroll: undefined,
})

const emit = defineEmits<{
  (e: 'change', value: StatisticCardSelectionValue | StatisticCardSelectionValue[]): void
  (e: 'update:value', value: StatisticCardSelectionValue | StatisticCardSelectionValue[]): void
}>()

// controlType 为可选，直接传 props 无法匹配 SelectionProps 判别联合，用 reactive 代理收敛类型并保持响应性。
const selectionProps = reactive({
  controlType: computed(() => (props.controlType === 'radio' || props.controlType === 'checkbox' ? props.controlType : null)),
  value: computed(() => props.value),
  maxLength: computed(() => props.maxLength),
  minLength: computed(() => props.minLength),
  onChange: (value: StatisticCardSelectionValue | StatisticCardSelectionValue[]) => emit('change', value),
  'onUpdate:value': (value: StatisticCardSelectionValue | StatisticCardSelectionValue[]) => emit('update:value', value),
}) as unknown as SelectionProps

const selection = useSelection(selectionProps)
// 顶层 ref 才会在模板中自动解包；直接写 `selection.controlType` 传给子组件
// 会把 ComputedRef 对象原样传下去，导致卡片内 `controlType === 'checkbox'` 判断失效。
const selectionControlType = selection.controlType

// inject 容器（Group / SuperGroup）注入的外观上下文，
// 对齐 React 端 cloneElement 的 `child.props.size ?? size` 语义：
// list 自身显式传入的外观 props 优先，否则回退到容器注入值，最后回退默认值。
const appearanceCtx = inject(statisticCardAppearanceKey, undefined)
const appearance = computed(() => appearanceCtx?.appearance.value)

const mergedSize = computed<StatisticCardSize>(() => props.size ?? appearance.value?.size ?? 'large')
const mergedReverseColor = computed(() => props.reverseColor ?? appearance.value?.reverseColor ?? false)
const mergedBorder = computed(() => props.border ?? appearance.value?.border ?? true)
const mergedEqualHeight = computed(() => props.equalHeight ?? appearance.value?.equalHeight ?? true)
const mergedNumberScroll = computed(() => props.numberScroll ?? appearance.value?.numberScroll)
const mergedSubIndicatorsLayout = computed(() => props.subIndicatorsLayout ?? appearance.value?.subIndicatorsLayout)

const hasPagination = computed(() => Boolean(props.pagination))
const columnCount = computed(() => Math.max(props.columns ?? 1, 1))
const paginationPlacement = computed(() => props.pagination?.buttonPlacement ?? 'both')
const cardMinWidth = computed(() => props.minCardWidth ?? CARD_MIN_WIDTH[mergedSize.value])
const layoutCardWidth = computed(() => typeof props.cardWidth === 'number' ? props.cardWidth : cardMinWidth.value)
const fillContainer = computed(() => props.cardWidth === 'auto' || props.cardWidth === undefined)

// useElementWidth 返回的是 { elRef, width }，此前误解构为 `ref` 导致 viewportRef 为 undefined，
// ResizeObserver 无法观察真实容器，viewportWidth 恒为 0 —— 引发分页每页仅 1 列、翻页无位移。
const { elRef: viewportRef, width: viewportWidth } = useElementWidth()

const maxVisibleColumns = computed(() => Math.max(Math.floor((viewportWidth.value + CARD_GAP) / (layoutCardWidth.value + CARD_GAP)), 1))
const visibleColumnCount = computed(() =>
  props.layout === 'grid' && hasPagination.value ? Math.min(columnCount.value, maxVisibleColumns.value) : columnCount.value,
)

const pageSize = computed(() =>
  resolvePageSize({
    pagination: props.pagination,
    dataLength: props.data.length,
    containerWidth: viewportWidth.value,
    minItemWidth: layoutCardWidth.value,
    gap: CARD_GAP,
    columns: props.layout === 'grid' ? visibleColumnCount.value : undefined,
  }),
)

const step = computed(() => props.pagination?.step ?? (props.layout === 'list' ? 'auto' : Math.max(pageSize.value, 1)))
const pageStartIndexes = computed(() => buildPageStarts(props.data.length, pageSize.value, step.value))
const pageIndex = ref(0)
const canPrev = computed(() => pageIndex.value > 0)
const canNext = computed(() => pageIndex.value < pageStartIndexes.value.length - 1)
const gridColumnWidth = computed(() =>
  props.cardWidth && props.cardWidth !== 'auto' ? props.cardWidth + 'px' : 'minmax(' + cardMinWidth.value + 'px, 1fr)',
)

watch(() => pageStartIndexes.value.length, (len) => {
  if (pageIndex.value > len - 1) pageIndex.value = 0
})

function goPrev() { pageIndex.value = Math.max(0, pageIndex.value - 1) }
function goNext() { pageIndex.value = Math.min(pageIndex.value + 1, pageStartIndexes.value.length - 1) }

// 显式声明 CSSProperties 返回类型，避免模板内 pointerEvents 三元结果被推断为宽 string 触发 TS2322
function pageStyle(pageOffset: number): CSSProperties {
  return { flex: '0 0 100%', minWidth: '0', pointerEvents: pageOffset === pageIndex.value ? 'auto' : 'none' }
}

function resolveCardItem(item: StatisticCardItemData): StatisticCardItemData {
  const disabled = item.disabled || selection.isSelectionDisabled(item.id)
  return disabled !== item.disabled ? { ...item, disabled } : item
}

function getCardStyle(shouldFlex: boolean): CSSProperties {
  const hasExplicitWidth = props.cardWidth != null && props.cardWidth !== 'auto'
  return {
    width: hasExplicitWidth ? props.cardWidth + 'px' : fillContainer.value && props.layout === 'list' ? 'auto' : undefined,
    minWidth: cardMinWidth.value + 'px',
    height: props.layout === 'grid' && mergedEqualHeight.value ? '100%' : undefined,
    flex: fillContainer.value && (props.layout === 'list' || shouldFlex) ? '1' : undefined,
  }
}

const rootStyle = computed<CSSProperties>(() => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: CARD_GAP + 'px',
  width: '100%',
}))

// provide card data context
provide(statisticCardDataKey, { data: computed(() => props.data) })
</script>
